// User Types
export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
}

export enum UserRole {
  ADMIN = 'admin',
  DSB = 'dsb', // Datenschutzbeauftragter
  MANAGER = 'manager',
  USER = 'user'
}

export interface UserCreate {
  email: string;
  password: string;
  name: string;
  role: UserRole;
}

export interface UserLogin {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

// Industry Enum
export enum Industry {
  IT = 'IT',
  HEALTHCARE = 'Healthcare',
  FINANCE = 'Finance',
  RETAIL = 'Retail',
  MANUFACTURING = 'Manufacturing',
  SERVICES = 'Services',
  EDUCATION = 'Education',
  GOVERNMENT = 'Government',
  OTHER = 'Other'
}

// Employee Count Enum
export enum EmployeeCount {
  SMALL = '1-10',
  MEDIUM = '11-50',
  LARGE = '51-250',
  XLARGE = '251-1000',
  ENTERPRISE = '1000+'
}

// Language Enum
export enum Language {
  DE = 'de',
  EN = 'en'
}

// Organization Types
export interface Organization {
  id: number;
  name: string;
  address: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string | null;
  dsb_name: string | null;
  dsb_email: string | null;
  dsb_phone: string | null;
  industry: Industry | null;
  employee_count: EmployeeCount | null;
  default_language: Language;
  default_retention_period: number;
  plan: OrganizationPlan;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationCreate {
  name: string;
  address: string;
  contact_person: string;
  contact_email: string;
  contact_phone?: string;
  dsb_name?: string;
  dsb_email?: string;
  dsb_phone?: string;
  industry?: Industry;
  employee_count?: EmployeeCount;
  default_language?: Language;
  default_retention_period?: number;
}

export interface OrganizationUpdate {
  name?: string;
  address?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  dsb_name?: string;
  dsb_email?: string;
  dsb_phone?: string;
  industry?: Industry;
  employee_count?: EmployeeCount;
  default_language?: Language;
  default_retention_period?: number;
}

// User Organization Relationship
export interface UserOrganization {
  user_id: number;
  organization_id: number;
  role: OrganizationRole;
}

export enum OrganizationRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer'
}

export enum OrganizationPlan {
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise'
}

export const PLAN_LIMITS = {
  [OrganizationPlan.STARTER]: {
    maxProcessingActivities: 10,
    maxServiceProviders: 10,
    maxTom: 10,
    maxDataSubjectRequests: 10,
    maxBreaches: 10,
    maxPrivacyNotices: 10,
    maxUsers: 1,
    serviceProviders: true,
    tom: true,
    dataSubjectRequests: true,
    breaches: true,
    approvals: true,
    auditLog: true,
    import: true,
  },
  [OrganizationPlan.PROFESSIONAL]: {
    maxProcessingActivities: Infinity,
    maxServiceProviders: Infinity,
    maxTom: Infinity,
    maxDataSubjectRequests: Infinity,
    maxBreaches: Infinity,
    maxPrivacyNotices: Infinity,
    maxUsers: 25,
    serviceProviders: true,
    tom: true,
    dataSubjectRequests: true,
    breaches: true,
    approvals: true,
    auditLog: true,
    import: true,
  },
  [OrganizationPlan.ENTERPRISE]: {
    maxProcessingActivities: Infinity,
    maxServiceProviders: Infinity,
    maxTom: Infinity,
    maxDataSubjectRequests: Infinity,
    maxBreaches: Infinity,
    maxPrivacyNotices: Infinity,
    maxUsers: Infinity,
    serviceProviders: true,
    tom: true,
    dataSubjectRequests: true,
    breaches: true,
    approvals: true,
    auditLog: true,
    import: true,
  },
} as const;

// Audit Log Types
export interface AuditLog {
  id: number;
  user_id: number;
  action: AuditAction;
  entity_type: EntityType;
  entity_id: number;
  changes: string; // JSON string
  created_at: string;
}

export enum AuditAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  VIEW = 'view',
  LOGIN = 'login',
  LOGOUT = 'logout'
}

export enum EntityType {
  USER = 'user',
  ORGANIZATION = 'organization',
  PROCESSING_ACTIVITY = 'processing_activity',
  DATA_CATEGORY = 'data_category',
  CONSENT = 'consent',
  BREACH = 'breach',
  SERVICE_PROVIDER = 'service_provider',
  AVV_CONTRACT = 'avv_contract',
  PRIVACY_NOTICE = 'privacy_notice',
  TOM_MEASURE = 'tom_measure',
  DATA_SUBJECT_REQUEST = 'data_subject_request',
  APPROVAL = 'approval'
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Request Types
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// JWT Payload
export interface JwtPayload {
  userId: number;
  email: string;
  role: UserRole;
  iat?: number;
  exp?: number;
}

// Processing Activity Types (VVT - Verzeichnis von Verarbeitungstätigkeiten)

// Rechtsgrundlagen nach Art. 6 DSGVO
export enum LegalBasis {
  CONSENT = 'consent',           // Einwilligung Art. 6(1)(a)
  CONTRACT = 'contract',         // Vertragserfüllung Art. 6(1)(b)
  LEGAL_OBLIGATION = 'legal',    // Rechtliche Verpflichtung Art. 6(1)(c)
  VITAL_INTERESTS = 'vital',     // Lebenswichtige Interessen Art. 6(1)(d)
  PUBLIC_INTEREST = 'public',    // Öffentliches Interesse Art. 6(1)(e)
  LEGITIMATE_INTEREST = 'legitimate' // Berechtigtes Interesse Art. 6(1)(f)
}

// Status einer Verarbeitung
export enum ProcessingStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  ARCHIVED = 'archived'
}

// Datenkategorien
export enum DataCategory {
  CONTACT = 'contact',           // Kontaktdaten
  IDENTIFICATION = 'identification', // Identifikationsdaten
  FINANCIAL = 'financial',       // Finanzdaten
  EMPLOYMENT = 'employment',     // Beschäftigungsdaten
  HEALTH = 'health',             // Gesundheitsdaten (Art. 9)
  BEHAVIORAL = 'behavioral',     // Verhaltensdaten
  LOCATION = 'location',         // Standortdaten
  COMMUNICATION = 'communication', // Kommunikationsdaten
  OTHER = 'other'
}

// Betroffenenkategorien
export enum DataSubjectCategory {
  EMPLOYEES = 'employees',
  CUSTOMERS = 'customers',
  PROSPECTS = 'prospects',
  SUPPLIERS = 'suppliers',
  APPLICANTS = 'applicants',
  WEBSITE_VISITORS = 'visitors',
  BUSINESS_PARTNERS = 'partners',
  OTHER = 'other'
}

// Risikolevel
export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high'
}

// Verarbeitungstätigkeit (VVT-Eintrag)
export interface ProcessingActivity {
  id: number;
  organization_id: number;
  name: string;
  department: string;
  description: string | null;
  purpose: string;
  legal_basis: LegalBasis;
  legal_basis_details: string | null;
  data_subjects: DataSubjectCategory[];
  data_categories: DataCategory[];
  recipients: string[];
  retention_period: number; // in Monaten
  retention_details: string | null;
  third_country_transfer: boolean;
  third_country_details: string | null;
  tom_reference: string | null;
  risk_level: RiskLevel | null;
  status: ProcessingStatus;
  version: number;
  created_by: number;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProcessingActivityCreate {
  organization_id: number;
  name: string;
  department: string;
  description?: string;
  purpose: string;
  legal_basis: LegalBasis;
  legal_basis_details?: string;
  data_subjects: DataSubjectCategory[];
  data_categories: DataCategory[];
  recipients: string[];
  retention_period: number;
  retention_details?: string;
  third_country_transfer: boolean;
  third_country_details?: string;
  tom_reference?: string;
  risk_level?: RiskLevel;
}

export interface ProcessingActivityUpdate {
  name?: string;
  department?: string;
  description?: string;
  purpose?: string;
  legal_basis?: LegalBasis;
  legal_basis_details?: string;
  data_subjects?: DataSubjectCategory[];
  data_categories?: DataCategory[];
  recipients?: string[];
  retention_period?: number;
  retention_details?: string;
  third_country_transfer?: boolean;
  third_country_details?: string;
  tom_reference?: string;
  risk_level?: RiskLevel;
}

export interface ProcessingActivityStatusUpdate {
  status: ProcessingStatus;
  approved_by?: number;
}

export interface ProcessingActivityStatistics {
  total: number;
  byStatus: {
    draft: number;
    in_review: number;
    approved: number;
    archived: number;
  };
  byRiskLevel: {
    low: number;
    medium: number;
    high: number;
    unassessed: number;
  };
  byLegalBasis: Record<LegalBasis, number>;
  thirdCountryTransfers: number;
}

export enum ProcessingTemplateCategory {
  HR = 'hr',
  SALES_CRM = 'sales_crm',
  MARKETING = 'marketing',
  FINANCE = 'finance',
  IT_SECURITY = 'it_security',
  FACILITY = 'facility',
  COMMUNICATION = 'communication',
  LEGAL_COMPLIANCE = 'legal_compliance',
  WEBSITE_DIGITAL = 'website_digital',
  GENERAL = 'general'
}

export interface ProcessingActivityTemplate {
  name: string;
  category: ProcessingTemplateCategory;
  department: string;
  description: string;
  purpose: string;
  legal_basis: LegalBasis;
  legal_basis_details: string;
  data_subjects: DataSubjectCategory[];
  data_categories: DataCategory[];
  recipients: string[];
  retention_period: number;
  retention_details: string;
  third_country_transfer: boolean;
  risk_level: RiskLevel;
}

export enum ServiceProviderTemplateCategory {
  CLOUD_HOSTING = 'cloud_hosting',
  HR_PAYROLL = 'hr_payroll',
  CRM_SALES = 'crm_sales',
  MARKETING_EMAIL = 'marketing_email',
  FINANCE_TAX = 'finance_tax',
  COMMUNICATION = 'communication',
  SECURITY_IT = 'security_it',
  ANALYTICS = 'analytics',
  OFFICE_PRODUCTIVITY = 'office_productivity',
  SHIPPING_LOGISTICS = 'shipping_logistics',
  PAYMENT = 'payment',
  LEGAL_COMPLIANCE = 'legal_compliance'
}

export interface ServiceProviderTemplate {
  name: string;
  category: ServiceProviderTemplateCategory;
  role: ServiceProviderRole;
  address: string;
  service_description: string;
  hosting_location: string;
  notes: string;
}

// AVV Document Types
export interface AvvDocument {
  name: string;
  path: string;
  uploaded_at: string;
}

// Service Provider Types
export enum ServiceProviderRole {
  PROCESSOR = 'processor',
  JOINT_CONTROLLER = 'joint_controller',
  RECIPIENT = 'recipient'
}

export enum AvvStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  TERMINATED = 'terminated'
}

export interface ServiceProvider {
  id: number;
  organization_id: number;
  name: string;
  address: string | null;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  service_description: string | null;
  hosting_location: string | null;
  role: ServiceProviderRole;
  notes: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  avv_contracts?: AvvContract[];
  linked_processing_count?: number;
}

export interface ServiceProviderCreate {
  organization_id: number;
  name: string;
  role: ServiceProviderRole;
  address?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  service_description?: string;
  hosting_location?: string;
  notes?: string;
}

export interface ServiceProviderUpdate {
  name?: string;
  role?: ServiceProviderRole;
  address?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  service_description?: string;
  hosting_location?: string;
  notes?: string;
  organization_id?: number;
}

export interface AvvContract {
  id: number;
  service_provider_id: number;
  version: string;
  start_date: string;
  end_date: string | null;
  status: AvvStatus;
  subcontractors: string[];
  tom_assessment: string | null;
  tom_adequate: boolean;
  approved_by: number | null;
  approved_at: string | null;
  document_reference: string | null;
  documents: AvvDocument[];
  local_path: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AvvContractCreate {
  service_provider_id: number;
  version: string;
  start_date: string;
  end_date?: string;
  status?: AvvStatus;
  subcontractors?: string[];
  tom_assessment?: string;
  tom_adequate?: boolean;
  document_reference?: string;
  local_path?: string;
  notes?: string;
}

export interface AvvContractUpdate {
  version?: string;
  start_date?: string;
  end_date?: string;
  subcontractors?: string[];
  tom_assessment?: string;
  tom_adequate?: boolean;
  document_reference?: string;
  local_path?: string;
  notes?: string;
}

export interface ServiceProviderStatistics {
  total: number;
  byRole: Record<string, number>;
  withActiveAvv: number;
  withExpiredAvv: number;
  withoutAvv: number;
  expiringWithin90Days: number;
}

// Privacy Notice Types (Datenschutzhinweise)

export enum PrivacyNoticeChannel {
  WEBSITE = 'website',
  APP = 'app',
  APPLICATION_FORM = 'application_form',
  EMPLOYMENT = 'employment',
  NEWSLETTER = 'newsletter',
  CUSTOM = 'custom'
}

export enum PrivacyNoticeStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  ARCHIVED = 'archived'
}

export enum PrivacyNoticeSectionType {
  CONTROLLER = 'controller',
  DPO = 'dpo',
  PURPOSE = 'purpose',
  RECIPIENTS = 'recipients',
  THIRD_COUNTRY = 'third_country',
  RETENTION = 'retention',
  RIGHTS = 'rights',
  COMPLAINT = 'complaint',
  OBLIGATION = 'obligation',
  SOURCE = 'source',
  CUSTOM = 'custom'
}

export interface PrivacyNotice {
  id: number;
  organization_id: number;
  title: string;
  channel: PrivacyNoticeChannel;
  language: Language;
  status: PrivacyNoticeStatus;
  current_version_id: number | null;
  created_by: number;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  current_version?: PrivacyNoticeVersion;
  processing_activities?: ProcessingActivity[];
  is_outdated?: boolean;
}

export interface PrivacyNoticeVersion {
  id: number;
  notice_id: number;
  version_number: number;
  valid_from: string | null;
  valid_until: string | null;
  generated_content: string | null;
  snapshot_hash: string | null;
  notes: string | null;
  created_by: number;
  created_at: string;
  sections?: PrivacyNoticeSection[];
}

export interface PrivacyNoticeSection {
  id: number;
  version_id: number;
  section_type: PrivacyNoticeSectionType;
  sort_order: number;
  heading: string;
  short_text: string;
  full_text: string;
  source_type: string | null;
  source_id: number | null;
  is_auto_generated: boolean;
  created_at: string;
  updated_at: string;
}

export interface PrivacyNoticeCreate {
  organization_id: number;
  title: string;
  channel: PrivacyNoticeChannel;
  language?: Language;
  processing_activity_ids: number[];
}

export interface PrivacyNoticeUpdate {
  title?: string;
  channel?: PrivacyNoticeChannel;
  language?: Language;
  organization_id?: number;
}

export interface PrivacyNoticeSectionUpdate {
  heading?: string;
  short_text?: string;
  full_text?: string;
  sort_order?: number;
}

export interface PrivacyNoticeStatistics {
  total: number;
  byStatus: {
    draft: number;
    in_review: number;
    approved: number;
    archived: number;
  };
  byChannel: Record<string, number>;
  outdated: number;
}

// TOM (Technische und Organisatorische Maßnahmen) Types

export enum TomCategory {
  ACCESS_CONTROL = 'access_control',             // Zugriffskontrolle
  TRANSFER_CONTROL = 'transfer_control',         // Weitergabekontrolle
  INPUT_CONTROL = 'input_control',               // Eingabekontrolle
  AVAILABILITY = 'availability',                 // Verfügbarkeit
  RESILIENCE = 'resilience',                     // Belastbarkeit
  RECOVERY = 'recovery',                         // Wiederherstellung
  SEPARATION = 'separation',                     // Trennbarkeit
  DELETION = 'deletion',                         // Löschung
  PRIVACY_BY_DESIGN = 'privacy_by_design',       // Privacy by Design/Default
  ENCRYPTION = 'encryption',                     // Verschlüsselung
  PSEUDONYMIZATION = 'pseudonymization'          // Pseudonymisierung
}

export enum TomStatus {
  PLANNED = 'planned',
  IMPLEMENTED = 'implemented',
  OUTDATED = 'outdated',
  COMPENSATING = 'compensating'
}

export enum TomScope {
  GLOBAL = 'global',
  PER_PROCESSING = 'per_processing'
}

export enum TomEffectiveness {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  NOT_ASSESSED = 'not_assessed'
}

export interface TomMeasure {
  id: number;
  organization_id: number;
  name: string;
  description: string | null;
  category: TomCategory;
  status: TomStatus;
  scope: TomScope;
  effectiveness: TomEffectiveness;
  responsible_person: string | null;
  review_date: string | null;
  evidence_reference: string | null;
  notes: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  linked_processing_count?: number;
}

export interface TomMeasureCreate {
  organization_id: number;
  name: string;
  description?: string;
  category: TomCategory;
  status?: TomStatus;
  scope?: TomScope;
  effectiveness?: TomEffectiveness;
  responsible_person?: string;
  review_date?: string;
  evidence_reference?: string;
  notes?: string;
}

export interface TomMeasureUpdate {
  name?: string;
  description?: string;
  category?: TomCategory;
  status?: TomStatus;
  scope?: TomScope;
  effectiveness?: TomEffectiveness;
  responsible_person?: string;
  review_date?: string;
  evidence_reference?: string;
  notes?: string;
}

export interface TomStatistics {
  total: number;
  byCategory: Record<string, number>;
  byStatus: {
    planned: number;
    implemented: number;
    outdated: number;
    compensating: number;
  };
  byEffectiveness: {
    high: number;
    medium: number;
    low: number;
    not_assessed: number;
  };
  reviewDueWithin30Days: number;
  processingWithoutTom: number;
}

export interface TomTemplate {
  name: string;
  description: string;
  category: TomCategory;
  scope: TomScope;
  effectiveness: TomEffectiveness;
}

// Betroffenenrechte (Data Subject Requests) Types – Art. 12-22 DSGVO

export enum DataSubjectRequestType {
  ACCESS = 'access',                   // Auskunft (Art. 15)
  RECTIFICATION = 'rectification',     // Berichtigung (Art. 16)
  ERASURE = 'erasure',                 // Löschung (Art. 17)
  RESTRICTION = 'restriction',         // Einschränkung (Art. 18)
  PORTABILITY = 'portability',         // Datenübertragbarkeit (Art. 20)
  OBJECTION = 'objection'              // Widerspruch (Art. 21)
}

export enum DataSubjectRequestStatus {
  NEW = 'new',
  IDENTITY_CHECK = 'identity_check',
  IN_PROGRESS = 'in_progress',
  QUERY_OPEN = 'query_open',           // Rückfrage offen
  APPROVAL = 'approval',              // Freigabe
  COMPLETED = 'completed',
  REJECTED = 'rejected'
}

export enum DataSubjectRequestChannel {
  EMAIL = 'email',
  LETTER = 'letter',
  PHONE = 'phone',
  IN_PERSON = 'in_person',
  WEB_FORM = 'web_form',
  OTHER = 'other'
}

export enum IdentityVerificationStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  FAILED = 'failed',
  NOT_REQUIRED = 'not_required'
}

export interface DataSubjectRequest {
  id: number;
  organization_id: number;
  request_type: DataSubjectRequestType;
  status: DataSubjectRequestStatus;
  channel: DataSubjectRequestChannel;

  // Betroffene Person
  subject_name: string;
  subject_email: string | null;
  subject_phone: string | null;
  subject_reference: string | null;  // Kundennummer, Personalnummer etc.

  // Identitätsprüfung
  identity_status: IdentityVerificationStatus;
  identity_notes: string | null;

  // Fristen
  received_at: string;               // Eingangsdatum
  deadline_at: string;               // Frist (standardmäßig +30 Tage)
  extended_deadline_at: string | null; // Verlängerte Frist (+60 Tage bei Komplexität)
  completed_at: string | null;

  // Inhalt
  description: string | null;        // Beschreibung der Anfrage
  internal_notes: string | null;     // Interne Notizen
  response_text: string | null;      // Antworttext an Betroffenen
  rejection_reason: string | null;   // Ablehnungsgrund

  // Metadaten
  created_by: number;
  assigned_to: number | null;
  created_at: string;
  updated_at: string;

  // Verknüpfungen (optional, für Detail-Ansicht)
  linked_processing_ids?: number[];
}

export interface DataSubjectRequestCreate {
  organization_id: number;
  request_type: DataSubjectRequestType;
  channel: DataSubjectRequestChannel;
  subject_name: string;
  subject_email?: string;
  subject_phone?: string;
  subject_reference?: string;
  received_at: string;
  description?: string;
  internal_notes?: string;
  assigned_to?: number;
}

export interface DataSubjectRequestUpdate {
  status?: DataSubjectRequestStatus;
  request_type?: DataSubjectRequestType;
  channel?: DataSubjectRequestChannel;
  subject_name?: string;
  subject_email?: string;
  subject_phone?: string;
  subject_reference?: string;
  identity_status?: IdentityVerificationStatus;
  identity_notes?: string;
  extended_deadline_at?: string;
  description?: string;
  internal_notes?: string;
  response_text?: string;
  rejection_reason?: string;
  assigned_to?: number;
}

export interface DataSubjectRequestStatistics {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  overdue: number;
  averageResponseDays: number | null;
}

// Datenschutzvorfälle (Data Breaches) – Art. 33, 34 DSGVO

export enum BreachStatus {
  DETECTED = 'detected',             // Entdeckt
  TRIAGE = 'triage',                 // Triage / Erstbewertung
  INVESTIGATION = 'investigation',   // Untersuchung
  AUTHORITY_NOTIFIED = 'authority_notified', // Behörde benachrichtigt
  SUBJECTS_NOTIFIED = 'subjects_notified',  // Betroffene benachrichtigt
  REMEDIATION = 'remediation',       // Gegenmaßnahmen
  CLOSED = 'closed'                  // Abgeschlossen
}

export enum BreachSeverity {
  LOW = 'low',                       // Gering — kein Risiko für Betroffene
  MEDIUM = 'medium',                 // Mittel — Risiko für Betroffene
  HIGH = 'high',                     // Hoch — hohes Risiko, Meldepflicht
  CRITICAL = 'critical'              // Kritisch — Benachrichtigung Betroffener
}

export enum BreachCategory {
  UNAUTHORIZED_ACCESS = 'unauthorized_access',     // Unbefugter Zugriff
  DATA_LOSS = 'data_loss',                         // Datenverlust
  UNAUTHORIZED_DISCLOSURE = 'unauthorized_disclosure', // Unbefugte Weitergabe
  ACCIDENTAL_DELETION = 'accidental_deletion',     // Versehentliche Löschung
  DEVICE_LOSS = 'device_loss',                     // Geräteverlust
  RANSOMWARE = 'ransomware',                       // Ransomware
  MISCONFIGURATION = 'misconfiguration',           // Fehlkonfiguration
  SOCIAL_ENGINEERING = 'social_engineering',       // Social Engineering
  OTHER = 'other'
}

export interface DataBreach {
  id: number;
  organization_id: number;
  title: string;
  status: BreachStatus;
  severity: BreachSeverity;
  category: BreachCategory;

  // Zeitpunkte
  incident_at: string;               // Zeitpunkt des Vorfalls
  detected_at: string;               // Zeitpunkt der Entdeckung
  authority_deadline: string;         // 72h-Frist ab detected_at
  authority_notified_at: string | null;
  subjects_notified_at: string | null;

  // Beschreibung
  description: string | null;        // Was ist passiert?
  affected_data: string | null;      // Welche Daten betroffen?
  affected_count: number | null;     // Geschätzte Anzahl Betroffener
  cause: string | null;              // Ursache
  consequences: string | null;       // Mögliche Folgen

  // Risikobewertung
  risk_assessment: string | null;    // Risikobewertung Freitext
  authority_required: boolean;       // Meldung an Behörde erforderlich?
  subjects_required: boolean;        // Benachrichtigung Betroffener erforderlich?

  // Meldungen
  authority_report: string | null;   // Meldetext an Behörde
  subjects_report: string | null;    // Benachrichtigungstext an Betroffene

  // Gegenmaßnahmen & Lessons Learned
  immediate_measures: string | null; // Sofortmaßnahmen
  remediation_plan: string | null;   // Abhilfeplan
  lessons_learned: string | null;    // Lessons Learned

  // Metadaten
  created_by: number;
  assigned_to: number | null;
  created_at: string;
  updated_at: string;
}

export interface DataBreachCreate {
  organization_id: number;
  title: string;
  category: BreachCategory;
  severity?: BreachSeverity;
  incident_at: string;
  detected_at: string;
  description?: string;
  affected_data?: string;
  affected_count?: number;
  cause?: string;
  assigned_to?: number;
}

export interface DataBreachUpdate {
  title?: string;
  status?: BreachStatus;
  severity?: BreachSeverity;
  category?: BreachCategory;
  incident_at?: string;
  detected_at?: string;
  description?: string;
  affected_data?: string;
  affected_count?: number;
  cause?: string;
  consequences?: string;
  risk_assessment?: string;
  authority_required?: boolean;
  subjects_required?: boolean;
  authority_report?: string;
  subjects_report?: string;
  immediate_measures?: string;
  remediation_plan?: string;
  lessons_learned?: string;
  assigned_to?: number;
}

export interface DataBreachStatistics {
  total: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
  openIncidents: number;
  authorityOverdue: number;        // Überfällige Behördenmeldungen
  averageResolutionDays: number | null;
}

export interface BreachTemplate {
  title: string;
  category: BreachCategory;
  severity: BreachSeverity;
  description: string;
  affected_data: string;
  immediate_measures: string;
}

// Freigabeworkflows (Approval Workflows)

export enum ApprovalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn'
}

export enum ApprovalEntityType {
  PROCESSING_ACTIVITY = 'processing_activity',
  PRIVACY_NOTICE = 'privacy_notice',
  AVV_CONTRACT = 'avv_contract',
  TOM_MEASURE = 'tom_measure',
  DATA_BREACH = 'data_breach'
}

export interface ApprovalRequest {
  id: number;
  organization_id: number;
  entity_type: ApprovalEntityType;
  entity_id: number;
  entity_name: string;          // Denormalized for list display
  requested_status: string;     // The target status being requested
  status: ApprovalStatus;
  comment: string | null;       // Requester's comment
  decision_comment: string | null; // Reviewer's decision comment
  requested_by: number;
  requested_by_name?: string;
  decided_by: number | null;
  decided_by_name?: string;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRequestCreate {
  organization_id: number;
  entity_type: ApprovalEntityType;
  entity_id: number;
  entity_name: string;
  requested_status: string;
  comment?: string;
}

export interface ApprovalDecision {
  status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED;
  decision_comment?: string;
}

export interface ApprovalStatistics {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  withdrawn: number;
  byEntityType: Record<string, number>;
}

// Onboarding (Datenschutz-Kompass)

export type OnboardingStepKey =
  | 'organization' | 'service_providers' | 'toms' | 'processing'
  | 'linking' | 'privacy_notices' | 'dsr_process' | 'breach_process' | 'team';

export interface OnboardingStepStatus {
  stepKey: OnboardingStepKey;
  completed: boolean;
  dismissed: boolean;
  manuallyConfirmed: boolean;
  progress: { current: number; target: number };
  completedAt: string | null;
}

export interface OnboardingStatus {
  organizationId: number;
  steps: OnboardingStepStatus[];
  overallProgress: number; // 0..100
}
